import api from "../api/config";
import { normalizeCompanyName } from "../utils/companyNameMapper";

/**
 * Returns a CSS class string based on the merchant lead status
 * @param {string} status - Lead status string
 * @returns {string} CSS class for status badge
 */
export const getStatusClass = (status) => {
  const statusMap = {
    Approved: "text-bg-success",
    New: "text-bg-info",
    Declined: "text-bg-danger",
    Closing: "text-bg-warning",
    "Not yet Interested": "text-bg-warning",
    "Dead End": "text-bg-danger",
    "Merchant Cancelled": "text-bg-danger",
    "Contacted/Have not Completed App": "text-bg-warning",
    "New Lead": "text-bg-warning",
    "Underwriting": "text-bg-warning",
  };
  return statusMap[status] || "text-bg-secondary";
};

/**
 * Formats raw lead data into a clean merchant object for UI
 * @param {object} lead - Raw lead object from API
 * @returns {object} Formatted merchant data
 */
export const formatMerchantData = (lead) => ({
  id: lead.id,
  businessName: lead.Company,
  Company: lead.Company,
  salesRep: lead.Sales_Agent !== "Select Sales Rep" ? lead.Sales_Agent : "",
  Sales_Agent: lead.Sales_Agent,
  Sales_Agent_Email: lead.Sales_Agent_Email ?? lead.salesRepEmail ?? "",
  accountEx: lead.Account_Executive !== "Select Account Executive" ? lead.Account_Executive : "",
  Account_Executive: lead.Account_Executive,
  email: lead.Email || "",
  Email: lead.Email,
  gwId: lead.Gateway_ID || "",
  Gateway_ID: lead.Gateway_ID,
  phone: lead.Phone || "",
  Phone: lead.Phone,
  dateSubmitted: lead.Created_Time?.split("T", 1)[0] || "",
  Created_Time: lead.Created_Time,
  dateApproved: lead.Date_Approved ? lead.Date_Approved.split("T", 1)[0] || "" : "",
  dateUpdated: lead.Last_Activity_Time?.split("T", 1)[0] || "",
  Last_Activity_Time: lead.Last_Activity_Time,
  status: lead.Lead_Status || "",
  Lead_Status: lead.Lead_Status,
  statusClass: getStatusClass(lead.Lead_Status),
  Submitting_Agent: lead.Submitting_Agent,
  Account_Rep: lead.Account_Rep,
  Full_Name: lead.Full_Name,
  First_Name: lead.First_Name ?? "",
  Last_Name: lead.Last_Name ?? "",
  Country_of_Formation: Array.isArray(lead.Country_of_Formation)
    ? lead.Country_of_Formation[0] ?? lead.country ?? ""
    : lead.Country_of_Formation ?? lead.country ?? "",
  Reason: lead.Reason,
  Notes1: lead.Notes1,
  Street_Address: lead.Street_Address || "",
  Savings: lead.Savings ?? lead.savings ?? "",
});

/**
 * Fetch merchant data from API with pagination and filters
 * @param {number} page - Current page number
 * @param {number} rowsPerPage - Number of rows per page
 * @param {object} filters - Filter object (company, salesRep, etc.)
 * @returns {Promise<{list: Array, info: object}>} Merchant list and pagination info
 */
export const fetchMerchantData = async (page = 1, rowsPerPage = 10, filters = {}) => {  
  // Only include defined filter values
  const cleanFilters = {};
  Object.keys(filters).forEach(key => {
    if (filters[key] !== undefined && filters[key] !== null && filters[key] !== '') {
      cleanFilters[key] = filters[key];
    }
  });

  // Normalize company name using centralized mapper
  if (cleanFilters.company) {
    cleanFilters.company = normalizeCompanyName(cleanFilters.company);
  }
  
  const body = {
    company: cleanFilters.company || "CyberSource",
    page,
    per_page: rowsPerPage,
    ...cleanFilters,
  };
  
  try {
    const response = await api.post("api/leads", body);
    const {
      data: { data: newZohoList, info },
    } = response;

    // Determine total records and pages
    const totalRecords = Math.max(info.count, newZohoList.length);

    return {
      list: newZohoList || [],
      info: {
        total_count: totalRecords,
        page,
        per_page: rowsPerPage,
        more_records: info.more_records,
      },
    };
  } catch (error) {
    console.error(`❌ API Call Failed - Page: ${page}`, error);
    throw error;
  }
};

/**
 * Categorize agents by company filter - returns only sales agents for that company
 * @param {Array} agents - List of all agents
 * @param {string} companyFilter - Company name to filter agents by
 * @returns {object} Categorized agents object with salesAgents array
 */
export const categorizeAgents = (agents, companyFilter = "") => {
  if (!agents || !Array.isArray(agents)) {
    return { salesAgents: [] };
  }

  const salesAgents = agents
    .filter((agent) => {
      // If no company filter, return all agents
      if (!companyFilter) return true;
      
      // Filter by company
      return agent.company === companyFilter;
    })
    .map((agent) => agent.name); // Return just the name as string

  return { salesAgents };
};
